import React from "react";
import { Box, Grid, TextField, Tooltip, Typography } from "@mui/material";
import { RotateCcw, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { ButtonIconOnly } from "src/UI/ButtonIconOnly";
import {
  extractGradientStops,
  isGradient,
  isHexColor,
  isRgbaTuple,
  normalizeHexTo6,
  replaceGradientStop,
  toCssColor,
} from "../constants";
import { ColorPickerInput, ColorSwatch, colorRowSx } from "./ColorPickerBox";

interface ColorsPanelProps {
  colors: Record<string, string>;
  originalColors: Record<string, string>;
  canDelete: boolean;
  onChange: (key: string, value: string) => void;
  onDelete: (key: string) => void;
}

const ColorField: React.FC<{
  name: string;
  value: string;
  onChange: (value: string) => void;
}> = ({ name, value, onChange }) => {
  const hex = isHexColor(value);
  const gradient = !hex && isGradient(value);
  const rgbaTuple = !hex && !gradient && isRgbaTuple(value);
  const stops = gradient ? extractGradientStops(value) : [];

  return (
    <Box sx={colorRowSx}>
      {hex && (
        <ColorPickerInput
          value={normalizeHexTo6(value)}
          ariaLabel={name}
          onChange={onChange}
        />
      )}
      {gradient && (
        <>
          <ColorSwatch gradient={value} />
          {stops.map((stop, i) => (
            <ColorPickerInput
              key={i}
              value={stop.hex6}
              ariaLabel={`${name} stop ${i + 1}`}
              onChange={(hex6) => onChange(replaceGradientStop(value, i, hex6))}
            />
          ))}
        </>
      )}
      {rgbaTuple && <ColorSwatch color={toCssColor(value)} />}
      <Tooltip title={name} placement="top-start">
        <TextField
          size="small"
          fullWidth
          label={name}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          inputProps={{ "aria-label": name }}
        />
      </Tooltip>
    </Box>
  );
};

const ColorsPanel: React.FC<ColorsPanelProps> = ({
  colors,
  originalColors,
  canDelete,
  onChange,
  onDelete,
}) => {
  const { t } = useTranslation(["corporate-brand-asset"]);

  const keys = Object.keys(colors);

  const sortGradientKeys = (gradientKeys: string[]): string[] =>
    [...gradientKeys].sort((a, b) => {
      const base = (k: string) => k.replace(/(Start|End)$/i, "");
      if (base(a) !== base(b)) return base(a).localeCompare(base(b));
      return /Start$/i.test(a) ? -1 : 1;
    });
  const belongsToGradients = (key: string) =>
    /^gradient/i.test(key) || isGradient(colors[key]);
  const gradientKeys = sortGradientKeys(keys.filter(belongsToGradients));
  const solidKeys = keys
    .filter((key) => !belongsToGradients(key))
    .sort((a, b) => a.localeCompare(b));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {keys.length === 0 && (
        <Typography variant="body2" color="text.primary">
          {t("colors.empty")}
        </Typography>
      )}

      {[
        { label: t("colors.groupSolid"), groupKeys: solidKeys },
        { label: t("colors.groupGradient"), groupKeys: gradientKeys },
      ]
        .filter((group) => group.groupKeys.length > 0)
        .map((group) => (
          <Box key={group.label}>
            <Typography
              variant="overline"
              color="text.primary"
              sx={{ display: "block", mb: 1 }}
            >
              {group.label}
            </Typography>
            <Grid container spacing={2}>
              {group.groupKeys.map((key) => {
                const originalValue = originalColors[key];
                const canReset =
                  originalValue !== undefined && originalValue !== colors[key];

                return (
                  <Grid item xs={12} md={6} key={key}>
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <ColorField
                          name={key}
                          value={colors[key]}
                          onChange={(value) => onChange(key, value)}
                        />
                      </Box>
                      <ButtonIconOnly
                        size="sm"
                        tooltip={
                          canReset ? String(t("colors.reset")) : undefined
                        }
                        aria-label={String(t("colors.reset"))}
                        disabled={!canReset}
                        onClick={() => onChange(key, originalValue)}
                        iconOnly={<RotateCcw size={16} />}
                        sx={{ mt: "4px" }}
                      />
                      {canDelete && (
                        <ButtonIconOnly
                          size="sm"
                          tooltip={String(t("colors.delete"))}
                          aria-label={String(t("colors.delete"))}
                          onClick={() => onDelete(key)}
                          iconOnly={<Trash2 size={16} />}
                          sx={{ mt: "4px" }}
                        />
                      )}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ))}
    </Box>
  );
};

export default ColorsPanel;
